import { useEffect } from 'react';

import { getCookie } from '@api/HelperAPI';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';

import { FAQs } from './dashboardWidgets/FAQs/FAQs';
import { TabularFlexiDashboard } from './dashboardWidgets/tabularFlexiDashboard/TabularFlexiDashboard';
import NewLoanEndedView from './EndedView/NewLoanEndedView';
import { useGetMultiLoanDashboardData } from './useGetMultiLoanDashboardData';
import { WidgetRenderer } from './WidgetRenderer/WidgetRenderer';
import { useTrackPageView } from '../../tracking/pageView';

const getAccountIdFromLocation = () => {
    const location = getCurrentLocation();
    const segments = location.pathname.split('/');
    return segments[2];
};

export const MultiLoanDashboard = () => {
    useTrackPageView({
        name: 'Flexi-12 Dashboard'
    });

    const accountId = getAccountIdFromLocation();

    const { features, isLoanEnded, loanStatus, topupStatus } =
        useGetMultiLoanDashboardData(accountId);

    useEffect(() => {
        if (features && accountId) {
            tracking.ampli.dashboardViewed({
                account_id: accountId,
                product_type: features.revenue_based_loan
                    ? 'merchant_cash_advance'
                    : 'credit_facility',
                //@ts-expect-error
                dashboard_state: loanStatus,
                topup_status: topupStatus
            });
        }
    }, [accountId, features, loanStatus, topupStatus]);

    if (!accountId) {
        return null;
    }

    if (isLoanEnded) {
        return (
            <div className="m-auto mt-[-50px] grid w-full grid-cols-7 gap-x-4xl gap-y-2xl bg-structure-100 p-5xl max-sm:px-xl min-l:px-[10%] min-xl:px-[15%]">
                <NewLoanEndedView accountId={accountId} />
            </div>
        );
    }
    if (!features) {
        return null;
    }

    /*
    In production, the dynamic dashboard is available only for DE customers. However, our tests for it are written in English.
    The cookie check below ensures that:
        - Tests can run in English by setting the `dynamicAccountPages` cookie to `true`, allowing the dynamic dashboard to be shown.
        - UK customers in production (who won't have the cookie set) continue to see the tabular dashboard.
    */
    if (getCountry() === 'UK' && getCookie('dynamicAccountPages') !== 'true') {
        return (
            <div
                data-testid="tabular-flexi-dashboard"
                className="m-auto mt-[-50px] block w-full grid-flow-dense grid-cols-7 gap-x-4xl gap-y-2xl space-y-l bg-transparent p-5xl min-sm:grid min-sm:space-y-0 max-sm:px-xl min-l:px-[10%] min-xl:px-[15%]"
            >
                <TabularFlexiDashboard accountId={accountId} />
            </div>
        );
    }

    return (
        <div
            className="bg-structure-100"
            data-testid="dynamic-multi-loan-dashboard"
        >
            <div className="mx-auto flex max-w-[1240px] flex-col gap-xl px-l py-[50px] min-l:flex-row">
                <div className="flex-1 space-y-2xl">
                    <WidgetRenderer accountId={accountId} features={features} />
                </div>
                <div className="flex-1 min-l:max-w-[375px]">
                    <FAQs accountId={accountId} />
                </div>
            </div>
        </div>
    );
};
